import './presence.css';
import type { PresenceUser, PresenceStatus } from '../net/protocol';
import { t, onLang } from './i18n';

/**
 * Online-players panel: a floating 👥 button that toggles a list of everyone
 * currently connected, with what they're doing (standby, in-game solo / vs AI /
 * 1v1, etc). The App owns the presence socket and feeds rows in via `setUsers`.
 * In-game players in a watchable room get a Watch button (App spectates the room).
 */
export interface PresencePanelCallbacks {
  onWatch(room: string): void;
  /** Start a private conversation with this player (opens a chat DM tab). */
  onWhisper?(name: string): void;
  /** Invite this player to a game (재대결) — the server opens a room if needed. */
  onRematch?(name: string): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

const STATUS_KEY: Record<PresenceStatus, string> = {
  standby: 'presence.standby',
  solo: 'presence.solo',
  ai: 'presence.ai',
  tutorial: 'presence.tutorial',
  waiting: 'presence.waiting',
  spectating: 'presence.spectating',
  'playing-1v1': 'presence.playing1v1',
  'playing-2v2': 'presence.playing2v2',
  'playing-1v1v1': 'presence.playing1v1v1',
  'playing-1v1v1v1': 'presence.playing1v1v1v1',
};
// busy = in a match (green dot); idle = standby/waiting (grey dot)
const isBusy = (s: PresenceStatus) => s.startsWith('playing') || s === 'spectating';

export class PresencePanel {
  private readonly fab = el('button', 'interactive');
  private readonly panel = el('div', 'hud-panel interactive');
  private readonly list = el('div', 'presence-list');
  private readonly title = el('div', 'presence-title');
  private users: PresenceUser[] = [];
  private myName = '';
  private inGame = false; // while in a game of your own, the Watch buttons are hidden
  private suppressClick = false; // a drag ends in a click we must not treat as a toggle

  constructor(root: HTMLElement, private cb?: PresencePanelCallbacks) {
    this.fab.id = 'presence-fab';

    this.panel.id = 'presence-panel';
    this.panel.style.display = 'none';
    this.panel.append(this.title, this.list);

    onLang(() => this.render()); // re-label status text on language change
    root.append(this.fab, this.panel);
    this.bindFabDrag(); // drag the toggle button to move it; the list follows it
    this.restorePos();
    // keep the button on-screen and the (open) list anchored to it on resize
    window.addEventListener('resize', () => {
      const r = this.fab.getBoundingClientRect();
      this.moveFab(r.left, r.top);
    });
    this.render();
  }

  /**
   * The toggle button is both the tap target (open/close) and the drag handle.
   * A small deadzone distinguishes a tap from a drag; while dragging, the open
   * list re-anchors to follow the button. Button position persists across sessions.
   */
  private bindFabDrag(): void {
    this.fab.style.touchAction = 'none';
    let downX = 0;
    let downY = 0;
    let startLeft = 0;
    let startTop = 0;
    let dragging = false;
    let moved = false;
    this.fab.addEventListener('pointerdown', (e) => {
      dragging = true;
      moved = false;
      const r = this.fab.getBoundingClientRect();
      startLeft = r.left;
      startTop = r.top;
      downX = e.clientX;
      downY = e.clientY;
      this.fab.setPointerCapture(e.pointerId);
    });
    this.fab.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - downX;
      const dy = e.clientY - downY;
      if (!moved && Math.hypot(dx, dy) > 4) moved = true; // deadzone → still a tap
      if (moved) this.moveFab(startLeft + dx, startTop + dy);
    });
    const end = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      try {
        this.fab.releasePointerCapture(e.pointerId);
      } catch {
        /* pointer already released */
      }
      if (moved) {
        this.savePos();
        this.suppressClick = true; // the trailing click after a drag must not toggle
      }
    };
    this.fab.addEventListener('pointerup', end);
    this.fab.addEventListener('pointercancel', end);
    this.fab.addEventListener('click', () => {
      if (this.suppressClick) {
        this.suppressClick = false;
        return;
      }
      const open = this.panel.style.display === 'none';
      this.panel.style.display = open ? 'flex' : 'none';
      if (open) this.positionPanel();
    });
  }

  /** Move the toggle button's top-left to (x, y), clamped into the viewport; the
   *  open list re-anchors to follow it. */
  private moveFab(x: number, y: number): void {
    const w = this.fab.offsetWidth || 60;
    const h = this.fab.offsetHeight || 32;
    const cx = Math.max(0, Math.min(window.innerWidth - w, x));
    const cy = Math.max(0, Math.min(window.innerHeight - h, y));
    this.fab.style.left = `${cx}px`;
    this.fab.style.top = `${cy}px`;
    this.fab.style.right = 'auto';
    this.fab.style.bottom = 'auto';
    this.positionPanel();
  }

  /** Anchor the (open) list to the toggle button — directly below it, or above
   *  when there's no room — clamped fully into the viewport. */
  private positionPanel(): void {
    if (this.panel.style.display === 'none') return;
    const fr = this.fab.getBoundingClientRect();
    const pw = this.panel.offsetWidth || 220;
    const ph = this.panel.offsetHeight || 0;
    let left = fr.left;
    let top = fr.bottom + 6;
    if (top + ph > window.innerHeight) top = fr.top - 6 - ph; // flip above if it won't fit below
    left = Math.max(0, Math.min(window.innerWidth - pw, left));
    top = Math.max(0, Math.min(window.innerHeight - ph, top));
    this.panel.style.left = `${left}px`;
    this.panel.style.top = `${top}px`;
    this.panel.style.right = 'auto';
    this.panel.style.bottom = 'auto';
  }

  private savePos(): void {
    try {
      // v2: key bumped when the default dock moved to the bottom-left corner, so
      // stale saved positions don't pin everyone to the old mid-left spot
      localStorage.setItem('carom.presencePos2', JSON.stringify({ left: this.fab.style.left, top: this.fab.style.top }));
    } catch {
      /* storage unavailable */
    }
  }

  private restorePos(): void {
    try {
      const raw = localStorage.getItem('carom.presencePos2');
      if (!raw) return;
      const p = JSON.parse(raw) as { left?: string; top?: string };
      if (p.left && p.top) {
        this.fab.style.left = p.left;
        this.fab.style.top = p.top;
        this.fab.style.right = 'auto';
        this.fab.style.bottom = 'auto';
      }
    } catch {
      /* ignore bad saved value */
    }
  }

  /** The local player's display name, so we can mark "you" in the list. */
  setMyName(name: string): void {
    this.myName = name;
    this.render();
  }

  setUsers(users: PresenceUser[]): void {
    this.users = users;
    this.render();
  }

  /** Hide the Watch buttons while the local player is in a game of their own. */
  setInGame(v: boolean): void {
    if (this.inGame === v) return;
    this.inGame = v;
    this.render();
  }

  private render(): void {
    const n = this.users.length;
    this.fab.textContent = `👥 ${n}`;
    this.fab.title = t('presence.title');
    this.title.textContent = `${t('presence.title')} · ${n}`;

    this.list.replaceChildren();
    for (const u of this.users) {
      const row = el('div', 'presence-row');
      const dot = el('span', `presence-dot ${isBusy(u.status) ? 'busy' : 'idle'}`);
      const name = el('span', 'presence-name');
      name.textContent = u.name + (u.name === this.myName ? ` (${t('presence.you')})` : '');
      const status = el('span', 'presence-status');
      status.textContent = t(STATUS_KEY[u.status] ?? 'presence.standby');
      row.append(dot, name, status);
      // anyone but yourself can be whispered, wherever either of you happens to be
      if (u.name !== this.myName && this.cb?.onWhisper) {
        const dm = el('button', 'presence-dm');
        dm.textContent = '💬';
        dm.title = t('chat.whisperBtn');
        dm.onclick = () => this.cb!.onWhisper!(u.name);
        row.append(dm);
      }
      // Someone free to play → offer them a game. Only for players who aren't
      // already in a match, and only when we aren't mid-game ourselves.
      if (!u.room && u.name !== this.myName && this.cb?.onRematch && !this.inGame && !isBusy(u.status)) {
        const again = el('button', 'presence-rematch');
        again.textContent = t('h2h.rematch');
        again.onclick = () => this.cb!.onRematch!(u.name);
        row.append(again);
      }
      // watchable live match (not our own row) → offer a Watch button, unless
      // we're in a game ourselves (you can't watch another match mid-game)
      if (u.room && u.name !== this.myName && this.cb && !this.inGame) {
        const watch = el('button', 'presence-watch');
        watch.textContent = t('lobby.watch');
        watch.onclick = () => this.cb!.onWatch(u.room!);
        row.append(watch);
      }
      this.list.appendChild(row);
    }
    if (n === 0) {
      const empty = el('div', 'presence-empty');
      empty.textContent = t('presence.empty');
      this.list.appendChild(empty);
    }
  }
}
